import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ClipboardCheck, LifeBuoy, Loader2, Mail, Plus, RefreshCw, Save } from 'lucide-react';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import axiosInstance from '../../services/axiosInstance';
// Le marche vengono da qui (non da data/takataMarche) perche' servono le CHIAVI
// minuscole con cui sono salvate e filtrate, piu' le quattro marche escluse dal
// richiamo — su cui un associato puo' comunque segnalare un problema.
import { MARCHE_SEGNALABILI, etichettaMarcaTakata } from '../../services/takataService';

type StatoSegnalazione = 'aperta' | 'presa_in_carico' | 'risolta';
type TipoSegnalazione =
  | 'non_apre' | 'telaio_non_compilato' | 'esito_non_letto'
  | 'esito_sbagliato' | 'captcha' | 'altro';

interface Segnalazione {
  _id: string;
  marca: string;
  tipo: TipoSegnalazione;
  descrizione?: string;
  vin?: string;
  esitoAtteso?: string;
  esitoRegistrato?: string;
  operatore?: string;
  associate?: { _id: string; nominativo?: string; email?: string } | string | null;
  stato: StatoSegnalazione;
  notaInterna?: string;
  // Ticket del Centro Ticket aperto da questa segnalazione («Apri ticket»).
  ticketId?: string;
  ticketNumber?: string;
  createdAt: string;
}

interface TestPortale {
  _id: string;
  marca: string;
  esito: 'ok' | 'non_funziona';
  nota?: string;
  eseguitoDa?: string;
  createdAt: string;
}

const TIPO_LABEL: Record<TipoSegnalazione, string> = {
  non_apre: 'Il portale non si apre',
  telaio_non_compilato: 'Telaio non compilato',
  esito_non_letto: 'Esito non letto',
  esito_sbagliato: 'ESITO SBAGLIATO',
  captcha: 'Captcha bloccante',
  altro: 'Altro',
};

const STATO_LABEL: Record<StatoSegnalazione, string> = {
  aperta: 'Aperta',
  presa_in_carico: 'Presa in carico',
  risolta: 'Risolta',
};

const STATO_BADGE: Record<StatoSegnalazione, string> = {
  aperta: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  presa_in_carico: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  risolta: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const BASE = '/admin/takata-stato-portali';

function fmt(d?: string) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

function nomeAssociato(s: Segnalazione) {
  if (s.associate && typeof s.associate === 'object') {
    return s.associate.nominativo || s.associate.email || '';
  }
  return s.operatore || '';
}

function emailAssociato(s: Segnalazione) {
  return (s.associate && typeof s.associate === 'object' && s.associate.email) || '';
}

// Collegamento «Scrivi allo studio»: apre il programma di posta dell'amministratore con
// oggetto e testo già pronti — la segnalazione citata per intero, così lo studio sa
// subito di cosa si parla. Niente invio dal server: la conversazione prosegue nella
// posta normale, dove ci sono le risposte.
function mailtoSegnalazione(s: Segnalazione) {
  const email = emailAssociato(s);
  if (!email) return '';
  const oggetto = `UNASCA — La tua segnalazione sulla verifica Takata (${etichettaMarcaTakata(s.marca)}${s.vin ? `, telaio ${s.vin}` : ''})`;
  const righe = [
    `Gentile ${nomeAssociato(s) || 'Associato'},`,
    '',
    `ti contattiamo a proposito della segnalazione inviata il ${fmt(s.createdAt)} dal registro della verifica Takata:`,
    '',
    `• Marca: ${etichettaMarcaTakata(s.marca)}`,
  ];
  if (s.vin) righe.push(`• Telaio: ${s.vin}`);
  righe.push(`• Problema: ${TIPO_LABEL[s.tipo] || s.tipo}`);
  if (s.descrizione) righe.push(`• Descrizione: ${s.descrizione}`);
  if (s.esitoAtteso) righe.push(`• Esito mostrato dal portale: ${s.esitoAtteso}`);
  if (s.esitoRegistrato) righe.push(`• Esito registrato: ${s.esitoRegistrato}`);
  righe.push('', '', '', 'Cordiali saluti,', 'UNASCA — Verifica richiamo airbag Takata');
  return `mailto:${email}?subject=${encodeURIComponent(oggetto)}&body=${encodeURIComponent(righe.join('\n'))}`;
}

const TakataStatoPortaliAdmin: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = usePermissions();
  // Solo chi ha takata.segnalazioni_manage puo' scrivere: senza il permesso
  // la pagina resta in sola lettura e i comandi di scrittura non vengono mostrati.
  const canManage = hasPermission('takata.segnalazioni_manage');
  // «Apri ticket» crea un ticket nel Centro Ticket: serve anche supporttickets.handle
  // (lo stesso permesso della creazione manuale dal Centro Ticket).
  const canOpenTicket = canManage && hasPermission('supporttickets.handle');

  // ?segnalazione=<id>: link dalla nota interna del ticket a una sola segnalazione.
  const [searchParams, setSearchParams] = useSearchParams();
  const segnalazioneId = searchParams.get('segnalazione') || '';

  const [items, setItems] = useState<Segnalazione[]>([]);
  const [loading, setLoading] = useState(true);
  const [stato, setStato] = useState('');
  const [marca, setMarca] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modifica in linea di una segnalazione
  const [editId, setEditId] = useState<string | null>(null);
  const [editStato, setEditStato] = useState<StatoSegnalazione>('aperta');
  const [editNota, setEditNota] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  // Apertura ticket dalla segnalazione
  const [ticketDaAprire, setTicketDaAprire] = useState<Segnalazione | null>(null);
  const [openingTicketId, setOpeningTicketId] = useState<string | null>(null);

  // Controlli periodici
  const [tests, setTests] = useState<TestPortale[]>([]);
  const [testMarca, setTestMarca] = useState('');
  const [testEsito, setTestEsito] = useState<'ok' | 'non_funziona'>('ok');
  const [testNota, setTestNota] = useState('');
  const [savingTest, setSavingTest] = useState(false);

  const loadSegnalazioni = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: 20 };
      // Con ?segnalazione= gli altri filtri si ignorano, così la segnalazione cercata compare.
      if (segnalazioneId) {
        params.segnalazione = segnalazioneId;
      } else {
        if (stato) params.stato = stato;
        if (marca) params.marca = marca;
      }
      const { data } = await axiosInstance.get(`${BASE}/segnalazioni`, { params });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch {
      toast.error('Errore nel caricamento delle segnalazioni');
    } finally {
      setLoading(false);
    }
    // `toast` non e' fra le dipendenze di proposito: se lo fosse, un errore di
    // caricamento mostrerebbe un toast, il toast farebbe ricreare questa funzione e
    // l'effetto che la osserva ripartirebbe, richiamando l'API che fallisce di nuovo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, stato, marca, segnalazioneId]);

  const loadTests = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${BASE}/test-portale`, { params: { limit: 30 } });
      setTests(Array.isArray(data.items) ? data.items : []);
    } catch {
      // La lista dei controlli non e' bloccante per la pagina.
    }
  }, []);

  useEffect(() => { loadSegnalazioni(); }, [loadSegnalazioni]);
  useEffect(() => { loadTests(); }, [loadTests]);

  const apriModifica = (s: Segnalazione) => {
    setEditId(s._id);
    setEditStato(s.stato);
    setEditNota(s.notaInterna || '');
  };

  const salvaModifica = async (id: string) => {
    setSavingId(id);
    try {
      const { data } = await axiosInstance.patch(`${BASE}/segnalazioni/${id}`, {
        stato: editStato,
        notaInterna: editNota,
      });
      setItems((prev) => prev.map((s) => (s._id === id ? { ...s, ...data.segnalazione } : s)));
      setEditId(null);
      toast.success('Segnalazione aggiornata');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSavingId(null);
    }
  };

  const apriTicket = async (s: Segnalazione) => {
    setOpeningTicketId(s._id);
    const collega = (ticketId?: string, ticketNumber?: string) => {
      if (!ticketId) return;
      setItems((prev) => prev.map((x) => (x._id === s._id ? { ...x, ticketId, ticketNumber } : x)));
    };
    try {
      const { data } = await axiosInstance.post(`${BASE}/segnalazioni/${s._id}/ticket`);
      collega(data.ticketId, data.ticketNumber);
      toast.success(`Ticket #${data.ticketNumber} aperto nel Centro Ticket`);
    } catch (e: any) {
      const d = e?.response?.data || {};
      // 409 con numero: il ticket esisteva gia' (altra scheda, altro operatore).
      if (e?.response?.status === 409 && d.ticketId) {
        collega(d.ticketId, d.ticketNumber);
        toast.info(`Ticket già aperto: #${d.ticketNumber}`);
      } else {
        toast.error(d.message || "Errore nell'apertura del ticket");
      }
    } finally {
      setOpeningTicketId(null);
    }
  };

  const registraTest = async () => {
    if (!testMarca) { toast.error('Scegli la marca'); return; }
    setSavingTest(true);
    try {
      await axiosInstance.post(`${BASE}/test-portale`, { marca: testMarca, esito: testEsito, nota: testNota });
      toast.success('Controllo registrato');
      setTestNota('');
      await loadTests();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore nel salvataggio del controllo');
    } finally {
      setSavingTest(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center flex-shrink-0">
          <ClipboardCheck className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Stato portali Takata</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Segnalazioni degli associati e controlli periodici sui portali</p>
        </div>
        <button onClick={() => { loadSegnalazioni(); loadTests(); }}
          className="ml-auto flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow">
          <RefreshCw className="w-4 h-4" /> Aggiorna
        </button>
      </div>

      {/* Avviso sola lettura: mostrato a chi non ha takata.segnalazioni_manage */}
      {!canManage && (
        <div className="mb-6 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          Sola lettura: per registrare controlli o aggiornare le segnalazioni serve il permesso <code className="text-xs">takata.segnalazioni_manage</code>.
        </div>
      )}

      {/* Registrazione controllo periodico - solo con permesso di scrittura */}
      {canManage && (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Registra un controllo periodico</h2>
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Marca</label>
            <select value={testMarca} onChange={(e) => setTestMarca(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
              <option value="">Seleziona…</option>
              {MARCHE_SEGNALABILI.map((m) => <option key={m.chiave} value={m.chiave}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Esito</label>
            <select value={testEsito} onChange={(e) => setTestEsito(e.target.value as 'ok' | 'non_funziona')}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
              <option value="ok">Funziona</option>
              <option value="non_funziona">Non funziona</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Nota</label>
            <input value={testNota} onChange={(e) => setTestNota(e.target.value)} maxLength={1000}
              placeholder="Cosa hai visto sul portale"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
          </div>
        </div>
        <button onClick={registraTest} disabled={savingTest || !testMarca}
          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">
          <Plus className="w-4 h-4" /> Registra controllo
        </button>
      </div>
      )}

      {segnalazioneId && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>Mostro solo la segnalazione collegata al ticket.</span>
          <button
            onClick={() => {
              const p = new URLSearchParams(searchParams);
              p.delete('segnalazione');
              setSearchParams(p, { replace: true });
              setPage(1);
            }}
            className="px-2 py-0.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            Mostra tutte
          </button>
        </div>
      )}

      {/* Filtri segnalazioni */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <select value={stato} onChange={(e) => { setPage(1); setStato(e.target.value); }}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
          <option value="">Tutti gli stati</option>
          <option value="aperta">Aperte</option>
          <option value="presa_in_carico">Prese in carico</option>
          <option value="risolta">Risolte</option>
        </select>
        <select value={marca} onChange={(e) => { setPage(1); setMarca(e.target.value); }}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
          <option value="">Tutte le marche</option>
          {MARCHE_SEGNALABILI.map((m) => <option key={m.chiave} value={m.chiave}>{m.label}</option>)}
        </select>
        <span className="sm:ml-auto self-center text-xs text-gray-500 dark:text-gray-400">{total} segnalazioni</span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nessuna segnalazione.</p>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s._id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{etichettaMarcaTakata(s.marca)}</span>
                <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${
                  s.tipo === 'esito_sbagliato'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {s.tipo === 'esito_sbagliato' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                  {TIPO_LABEL[s.tipo] || s.tipo}
                </span>
                {s.vin && <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{s.vin}</span>}
                <span className={`ml-auto text-2xs px-1.5 py-0.5 rounded-full font-medium ${STATO_BADGE[s.stato]}`}>
                  {STATO_LABEL[s.stato] || s.stato}
                </span>
              </div>

              {s.descrizione && <p className="text-sm text-gray-700 dark:text-gray-300 mt-1.5">{s.descrizione}</p>}

              {s.tipo === 'esito_sbagliato' && (s.esitoAtteso || s.esitoRegistrato) && (
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                  Portale: <strong>{s.esitoAtteso || '—'}</strong> · Registrato: <strong>{s.esitoRegistrato || '—'}</strong>
                </p>
              )}

              <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1.5">
                {fmt(s.createdAt)}{nomeAssociato(s) ? ` · ${nomeAssociato(s)}` : ''}
                {emailAssociato(s) && <span className="ml-1 text-gray-500 dark:text-gray-400">· {emailAssociato(s)}</span>}
              </p>

              {canManage && editId === s._id ? (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 grid sm:grid-cols-3 gap-2">
                  <select value={editStato} onChange={(e) => setEditStato(e.target.value as StatoSegnalazione)}
                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
                    <option value="aperta">Aperta</option>
                    <option value="presa_in_carico">Presa in carico</option>
                    <option value="risolta">Risolta</option>
                  </select>
                  <input value={editNota} onChange={(e) => setEditNota(e.target.value)} maxLength={2000}
                    placeholder="Nota interna"
                    className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
                  <div className="sm:col-span-3 flex gap-2">
                    <button onClick={() => salvaModifica(s._id)} disabled={savingId === s._id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">
                      <Save className="w-4 h-4" /> Salva
                    </button>
                    <button onClick={() => setEditId(null)}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm">
                      Annulla
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {s.notaInterna && <span className="text-xs text-gray-500 dark:text-gray-400">Nota interna: {s.notaInterna}</span>}
                  {s.ticketId && (
                    <Link to={`/admin/centro-ticket?ticket=${s.ticketId}`}
                      className="flex items-center gap-1 text-2xs px-1.5 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 hover:underline">
                      <LifeBuoy className="w-3 h-3" /> Ticket #{s.ticketNumber}
                    </Link>
                  )}
                  {/* Scrive allo studio col programma di posta, segnalazione citata per
                      intero. Il mailto non passa dal server: niente log, niente invii
                      automatici, la risposta arriva nella posta normale. */}
                  {emailAssociato(s) && (
                    <a href={mailtoSegnalazione(s)}
                      className="ml-auto flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30">
                      <Mail className="w-3.5 h-3.5" /> Scrivi allo studio
                    </a>
                  )}
                  {/* Ticket nel canale predefinito del Centro Ticket: uno per segnalazione */}
                  {canOpenTicket && !s.ticketId && (
                    <button onClick={() => setTicketDaAprire(s)} disabled={openingTicketId === s._id}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 disabled:opacity-50 ${emailAssociato(s) ? '' : 'ml-auto'}`}>
                      {openingTicketId === s._id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <LifeBuoy className="w-3.5 h-3.5" />}
                      {openingTicketId === s._id ? 'Apertura…' : 'Apri ticket'}
                    </button>
                  )}
                  {/* Cambio stato e nota interna: solo con permesso di scrittura */}
                  {canManage && (
                    <button onClick={() => apriModifica(s)}
                      className={`text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${emailAssociato(s) || (canOpenTicket && !s.ticketId) ? '' : 'ml-auto'}`}>
                      Gestisci
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 text-gray-700 dark:text-gray-300">Precedente</button>
          <span className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 text-gray-700 dark:text-gray-300">Successivo</button>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!ticketDaAprire}
        onClose={() => setTicketDaAprire(null)}
        onConfirm={() => { if (ticketDaAprire) apriTicket(ticketDaAprire); }}
        title="Aprire un ticket?"
        message={ticketDaAprire
          ? `Il ticket per la segnalazione ${etichettaMarcaTakata(ticketDaAprire.marca)}${nomeAssociato(ticketDaAprire) ? ` di ${nomeAssociato(ticketDaAprire)}` : ''} andrà nel canale predefinito del Centro Ticket.`
          : ''}
        confirmText="Apri ticket"
        type="info"
      />

      {/* Ultimi controlli registrati */}
      <h2 className="text-sm font-bold text-gray-900 dark:text-white mt-8 mb-2">Ultimi controlli registrati ({tests.length})</h2>
      {tests.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nessun controllo registrato.</p>
      ) : (
        <div className="space-y-1.5">
          {tests.map((t) => (
            <div key={t._id} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
              {t.esito === 'ok'
                ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                : <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{etichettaMarcaTakata(t.marca)}</span>
                {t.nota && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{t.nota}</span>}
              </div>
              <span className="text-2xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                {fmt(t.createdAt)}{t.eseguitoDa ? ` · ${t.eseguitoDa}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TakataStatoPortaliAdmin;
